import { useSyncExternalStore } from "react";
import { isTerminalGroup } from "./terminal-groups";

/**
 * Настройки терминала TigerTrade (§10 1.36, design.md D2) в браузере
 * трейдера: терминал — свойство машины, а не демона, поэтому ни в поток, ни
 * в базу они не уходят. Доступ к хранилищу собран здесь и обёрнут в
 * `try/catch` так же, как в `density/card-row-order.ts`: приватное окно или
 * запрет хранилища бросают исключение, оно глотается без журнала — настройки
 * тогда живут до перезагрузки.
 */

export const TERMINAL_SETTINGS_STORAGE_KEY = "screener.terminal.tigertrade";

/** Значения `e` и `m` команды `setLinkSymbol`. */
export interface TerminalMapping {
  e: string;
  m: string;
}

/** Какой терминал получает инструмент (§89 п. 1). */
export type TerminalKind = "tigertrade" | "metascalp";

/** Коды `exchange` и `market` запроса `change-ticker` MetaScalp. */
export interface MetascalpMapping {
  exchange: number;
  market: number;
}

/** Связки окон MetaScalp — номера 1–500 (§89 п. 6). */
export const MIN_BINDING = 1;
export const MAX_BINDING = 500;

export interface MetascalpSettings {
  /** `null` — порт ищется опросом `/ping` (§89 п. 3). */
  port: number | null;
  /** Ключ `"<exchange>:<market>"`, как у `TerminalSettings.mapping`. */
  mapping: Record<string, MetascalpMapping>;
  /** Упорядоченный набор связок для круга; пустой — запрос без `binding`. */
  bindings: number[];
}

export interface TerminalSettings {
  /** Выбранный терминал; сохранённое до 1.76 читается как TigerTrade. */
  terminal: TerminalKind;
  /** Общий выключатель действия для обоих терминалов. */
  enabled: boolean;
  host: string;
  port: number;
  /**
   * Наш рынок → значения терминала. Ключ — `"<exchange>:<market>"`, где
   * `exchange` — id строки инструмента (`instrument.exchange`), `market` — `spot` или `perp`. Нет ключа — нет и
   * действия «открыть в терминале» для этого рынка.
   */
  mapping: Record<string, TerminalMapping>;
  /**
   * Упорядоченный набор групп окон TigerTrade, по которому идёт круг
   * отправок (§10 1.45). Без повторов, каждая группа из 38. Пустой набор —
   * значение по умолчанию: отправить некуда, действие ведёт в настройки.
   */
  groups: string[];
  metascalp: MetascalpSettings;
}

const DEFAULT_HOST = "127.0.0.1";
const DEFAULT_PORT = 7819;
export const MIN_PORT = 1;
export const MAX_PORT = 65535;

/**
 * Соответствие по умолчанию — ПРЕДПОЛОЖЕНИЕ по документации TigerTrade (список
 * значений `e`), не проверенное живьём (design.md D2, `knowledge/terminals.md`).
 * У Binance perp в документации два варианта, `BINANCE`+`FUTURES` (пример
 * команды) и `BINANCE-FUT` (список); взят пример. Трейдер правит таблицу в
 * настройках. `BINANCE`+`SPOT` для спота — тоже предположение по документации
 * TigerTrade. HyperLiquid, Coinbase и Kraken в списке терминала нет.
 *
 * MEXC, Gate.io, Bitget (ТЗ 1.96 §109) — ПРЕДПОЛОЖЕНИЕ по документации Tiger
 * API (список бирж `e`, 15.10.2026): пример документации подтверждает только
 * Bitget/FUTURES, прочие пары e+m — по аналогии с Bybit/OKX. KuCoin, HTX,
 * BingX, AsterDex и Lighter в списке TigerTrade нет.
 */
const DEFAULT_MAPPING: Record<string, TerminalMapping> = {
  "binance:perp": { e: "BINANCE", m: "FUTURES" },
  "binance:spot": { e: "BINANCE", m: "SPOT" },
  "bybit:perp": { e: "BYBIT", m: "FUTURES" },
  "bybit:spot": { e: "BYBIT", m: "SPOT" },
  "okx:perp": { e: "OKX", m: "FUTURES" },
  "okx:spot": { e: "OKX", m: "SPOT" },
  "mexc:perp": { e: "MEXC", m: "FUTURES" },
  "mexc:spot": { e: "MEXC", m: "SPOT" },
  "gate:perp": { e: "Gate.io", m: "FUTURES" },
  "gate:spot": { e: "Gate.io", m: "SPOT" },
  "bitget:perp": { e: "Bitget", m: "FUTURES" },
  "bitget:spot": { e: "Bitget", m: "SPOT" },
};

/**
 * Версия таблицы умолчаний. При добавлении ключа в `DEFAULT_MAPPING`
 * поднимается версия и пополняется `DEFAULT_MAPPING_ADDITIONS`, иначе
 * сохранённые таблицы его не получат: удалённую трейдером строку от
 * никогда не существовавшей по самой таблице не отличить.
 */
const DEFAULT_MAPPING_VERSION = 3;

/**
 * Версия → ключи, появившиеся в умолчаниях в этой версии. Версия 1 — исходные
 * пять рынков (без binance:spot); версия 2 — binance:spot (stage8, Р11);
 * версия 3 — новые биржи этапа 9, ТЗ 1.96 §109.
 */
const DEFAULT_MAPPING_ADDITIONS: Record<number, string[]> = {
  2: ["binance:spot"],
  3: ["mexc:perp", "mexc:spot", "gate:perp", "gate:spot", "bitget:perp", "bitget:spot"],
};

/**
 * Умолчания §89 п. 4 и §109. Coinbase и Kraken в MetaScalp нет. Коды новых
 * бирж (gate 3, kucoin 5, bitget 7, mexc 8, bingx 11, htx 12, aster 17,
 * lighter 19) — ПРЕДПОЛОЖЕНИЕ по MetaScalp Linking API (15.10.2026), не
 * проверенное живьём; market 2 для бессрочных — «most common choice for
 * perpetual futures» по документации.
 */
const DEFAULT_METASCALP_MAPPING: Record<string, MetascalpMapping> = {
  "binance:spot": { exchange: 2, market: 0 },
  "binance:perp": { exchange: 2, market: 2 },
  "bybit:spot": { exchange: 4, market: 0 },
  "bybit:perp": { exchange: 4, market: 2 },
  "okx:spot": { exchange: 10, market: 0 },
  "okx:perp": { exchange: 10, market: 2 },
  "hyperliquid:perp": { exchange: 15, market: 2 },
  "gate:spot": { exchange: 3, market: 0 },
  "gate:perp": { exchange: 3, market: 2 },
  "kucoin:spot": { exchange: 5, market: 0 },
  "kucoin:perp": { exchange: 5, market: 2 },
  "bitget:spot": { exchange: 7, market: 0 },
  "bitget:perp": { exchange: 7, market: 2 },
  "mexc:spot": { exchange: 8, market: 0 },
  "mexc:perp": { exchange: 8, market: 2 },
  "bingx:spot": { exchange: 11, market: 0 },
  "bingx:perp": { exchange: 11, market: 2 },
  "htx:spot": { exchange: 12, market: 0 },
  "htx:perp": { exchange: 12, market: 2 },
  "aster:spot": { exchange: 17, market: 0 },
  "aster:perp": { exchange: 17, market: 2 },
  "lighter:spot": { exchange: 19, market: 0 },
  "lighter:perp": { exchange: 19, market: 2 },
};

/**
 * Версия таблицы умолчаний MetaScalp (как `DEFAULT_MAPPING_VERSION` у
 * TigerTrade): новый рынок в умолчаниях поднимает версию и пополняет
 * `DEFAULT_METASCALP_MAPPING_ADDITIONS`. Версия 1 — таблица §89 п. 4;
 * версия 2 — восемь новых бирж этапа 9, ТЗ 1.96 §109.
 */
const DEFAULT_METASCALP_MAPPING_VERSION = 2;
const DEFAULT_METASCALP_MAPPING_ADDITIONS: Record<number, string[]> = {
  2: [
    "gate:spot",
    "gate:perp",
    "kucoin:spot",
    "kucoin:perp",
    "bitget:spot",
    "bitget:perp",
    "mexc:spot",
    "mexc:perp",
    "bingx:spot",
    "bingx:perp",
    "htx:spot",
    "htx:perp",
    "aster:spot",
    "aster:perp",
    "lighter:spot",
    "lighter:perp",
  ],
};

export const DEFAULT_METASCALP_SETTINGS: MetascalpSettings = {
  port: null,
  mapping: DEFAULT_METASCALP_MAPPING,
  bindings: [],
};

export const DEFAULT_TERMINAL_SETTINGS: TerminalSettings = {
  terminal: "tigertrade",
  enabled: false,
  host: DEFAULT_HOST,
  port: DEFAULT_PORT,
  mapping: DEFAULT_MAPPING,
  groups: [],
  metascalp: DEFAULT_METASCALP_SETTINGS,
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function parseMappingEntry(value: unknown): TerminalMapping | null {
  if (!isRecord(value)) {
    return null;
  }
  const { e, m } = value;
  if (typeof e !== "string" || e.trim() === "" || typeof m !== "string" || m.trim() === "") {
    return null;
  }
  return { e, m };
}

function parseMapping(value: unknown): Record<string, TerminalMapping> {
  if (!isRecord(value)) {
    return DEFAULT_MAPPING;
  }
  const result: Record<string, TerminalMapping> = {};
  for (const [key, entry] of Object.entries(value)) {
    const parsed = parseMappingEntry(entry);
    if (parsed !== null) {
      result[key] = parsed;
    }
  }
  return result;
}

/** Нет поля или оно нечитаемо — версия 1 («до Р11»). */
function parseMappingVersion(value: unknown): number {
  return typeof value === "number" && Number.isInteger(value) && value >= 1 ? value : 1;
}

/**
 * Дописывает ключи умолчаний, появившиеся после сохранённой версии;
 * существующее не трогает. Один механизм на таблицу TigerTrade и таблицу
 * MetaScalp.
 */
export function addNewerDefaults<Entry>(
  mapping: Record<string, Entry>,
  savedVersion: number,
  table: {
    defaults: Record<string, Entry>;
    currentVersion: number;
    additions: Record<number, string[]>;
  },
): Record<string, Entry> {
  const result = { ...mapping };
  for (let version = savedVersion + 1; version <= table.currentVersion; version += 1) {
    for (const key of table.additions[version] ?? []) {
      const fallback = table.defaults[key];
      if (!(key in result) && fallback !== undefined) {
        result[key] = fallback;
      }
    }
  }
  return result;
}

/** Нет поля, не массив, повтор или чужая группа — пустой набор целиком. */
function parseGroups(value: unknown): string[] {
  if (!Array.isArray(value)) {
    return [];
  }
  const seen = new Set<string>();
  for (const item of value) {
    if (typeof item !== "string" || !isTerminalGroup(item) || seen.has(item)) {
      return [];
    }
    seen.add(item);
  }
  return [...seen];
}

function isCode(value: unknown): value is number {
  return typeof value === "number" && Number.isInteger(value) && value >= 0;
}

function parseMetascalpMapping(
  value: unknown,
  savedVersion: number,
): Record<string, MetascalpMapping> {
  if (!isRecord(value)) {
    return DEFAULT_METASCALP_MAPPING;
  }
  const result: Record<string, MetascalpMapping> = {};
  for (const [key, entry] of Object.entries(value)) {
    if (isRecord(entry) && isCode(entry.exchange) && isCode(entry.market)) {
      result[key] = { exchange: entry.exchange, market: entry.market };
    }
  }
  return addNewerDefaults(result, savedVersion, {
    defaults: DEFAULT_METASCALP_MAPPING,
    currentVersion: DEFAULT_METASCALP_MAPPING_VERSION,
    additions: DEFAULT_METASCALP_MAPPING_ADDITIONS,
  });
}

/** Нет поля, не массив, повтор или номер вне 1–500 — пустой набор целиком. */
function parseBindings(value: unknown): number[] {
  if (!Array.isArray(value)) {
    return [];
  }
  const seen = new Set<number>();
  for (const item of value) {
    if (
      typeof item !== "number" ||
      !Number.isInteger(item) ||
      item < MIN_BINDING ||
      item > MAX_BINDING ||
      seen.has(item)
    ) {
      return [];
    }
    seen.add(item);
  }
  return [...seen];
}

function parseMetascalp(value: unknown): MetascalpSettings {
  if (!isRecord(value)) {
    return DEFAULT_METASCALP_SETTINGS;
  }
  const { port, mapping, bindings, mappingVersion } = value;
  return {
    port:
      typeof port === "number" && Number.isInteger(port) && port >= MIN_PORT && port <= MAX_PORT
        ? port
        : null,
    mapping: parseMetascalpMapping(mapping, parseMappingVersion(mappingVersion)),
    bindings: parseBindings(bindings),
  };
}

function parseSettings(raw: string | null): TerminalSettings {
  if (raw === null) {
    return DEFAULT_TERMINAL_SETTINGS;
  }
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return DEFAULT_TERMINAL_SETTINGS;
  }
  if (!isRecord(parsed)) {
    return DEFAULT_TERMINAL_SETTINGS;
  }
  const { terminal, enabled, host, port, mapping, mappingVersion, groups, metascalp } = parsed;
  return {
    terminal: terminal === "metascalp" ? "metascalp" : "tigertrade",
    enabled: typeof enabled === "boolean" ? enabled : DEFAULT_TERMINAL_SETTINGS.enabled,
    host: typeof host === "string" && host.trim() !== "" ? host : DEFAULT_HOST,
    port:
      typeof port === "number" && Number.isInteger(port) && port >= MIN_PORT && port <= MAX_PORT
        ? port
        : DEFAULT_PORT,
    mapping:
      mapping === undefined
        ? DEFAULT_MAPPING
        : addNewerDefaults(parseMapping(mapping), parseMappingVersion(mappingVersion), {
            defaults: DEFAULT_MAPPING,
            currentVersion: DEFAULT_MAPPING_VERSION,
            additions: DEFAULT_MAPPING_ADDITIONS,
          }),
    groups: parseGroups(groups),
    metascalp: parseMetascalp(metascalp),
  };
}

/**
 * Читает настройки. Хранилище — параметр, а не `window.localStorage`
 * напрямую, чтобы тест мог подставить своё, включая бросающее исключение.
 * Испорченное значение даёт умолчания целиком, неполное — по полям: годные
 * поля берутся, остальные по умолчанию.
 */
export function readTerminalSettings(storage: Storage): TerminalSettings {
  try {
    return parseSettings(storage.getItem(TERMINAL_SETTINGS_STORAGE_KEY));
  } catch {
    return DEFAULT_TERMINAL_SETTINGS;
  }
}

/** Пишет настройки; исключение (приватное окно, запрет) глотается. */
export function writeTerminalSettings(storage: Storage, value: TerminalSettings): void {
  try {
    storage.setItem(
      TERMINAL_SETTINGS_STORAGE_KEY,
      JSON.stringify({
        ...value,
        mappingVersion: DEFAULT_MAPPING_VERSION,
        metascalp: { ...value.metascalp, mappingVersion: DEFAULT_METASCALP_MAPPING_VERSION },
      }),
    );
  } catch {
    // Хранилище недоступно — настройки живут до перезагрузки.
  }
}

/** Значения `e`/`m` для биржи и рынка строки; `null` — соответствия нет, действия нет. */
export function mappingFor(
  settings: TerminalSettings,
  exchange: string,
  market: string,
): TerminalMapping | null {
  return settings.mapping[`${exchange}:${market}`] ?? null;
}

/** Коды MetaScalp для биржи и рынка строки; `null` — соответствия нет, действия нет. */
export function metascalpMappingFor(
  settings: TerminalSettings,
  exchange: string,
  market: string,
): MetascalpMapping | null {
  return settings.metascalp.mapping[`${exchange}:${market}`] ?? null;
}

export interface TerminalSettingsStore {
  get: () => TerminalSettings;
  set: (next: TerminalSettings) => void;
  subscribe: (listener: () => void) => () => void;
}

/**
 * Хранилище настроек на вкладку. Настройки читают сразу несколько мест —
 * соединение, кнопка строки, лист настроек, — и у каждого своя копия через
 * `useState` разошлась бы. Поэтому значение одно, на подписках, под
 * `useSyncExternalStore`. Доступ к `localStorage` приходит функцией: сам
 * `window.localStorage` тоже может бросить, и тест подставляет своё.
 */
export function createTerminalSettingsStore(
  getStorage: () => Storage | null,
): TerminalSettingsStore {
  const listeners = new Set<() => void>();
  let current: TerminalSettings | null = null;

  const storage = (): Storage | null => {
    try {
      return getStorage();
    } catch {
      return null;
    }
  };

  return {
    get() {
      if (current === null) {
        const target = storage();
        current = target === null ? DEFAULT_TERMINAL_SETTINGS : readTerminalSettings(target);
      }
      return current;
    },
    set(next) {
      current = next;
      const target = storage();
      if (target !== null) {
        writeTerminalSettings(target, next);
      }
      for (const listener of listeners) {
        listener();
      }
    },
    subscribe(listener) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
  };
}

export const terminalSettingsStore = createTerminalSettingsStore(() => window.localStorage);

/**
 * Настройки терминала для компонента. Значение общее на вкладку (см.
 * `createTerminalSettingsStore`), запись — в сеттер, он же пишет в браузер.
 */
export function useTerminalSettings(): [TerminalSettings, (next: TerminalSettings) => void] {
  const settings = useSyncExternalStore(
    terminalSettingsStore.subscribe,
    terminalSettingsStore.get,
    terminalSettingsStore.get,
  );
  return [settings, terminalSettingsStore.set];
}
